/**
 * Small, NaN-proof formatters shared by the Web Work Mix panels. Everything
 * that reaches the DOM goes through here so a missing or non-finite number
 * renders as an em dash rather than "NaN" / "Infinity" / "undefined".
 */

export const DASH = '—';

export const fmtInt = (v: number | null | undefined): string =>
  typeof v === 'number' && Number.isFinite(v)
    ? Math.round(v).toLocaleString('en-GB')
    : DASH;

export const fmtPct = (v: number | null | undefined, digits = 0): string =>
  typeof v === 'number' && Number.isFinite(v) ? `${v.toFixed(digits)}%` : DASH;

/** Share of `part` in `whole` as "12%"; dash when the whole is 0. */
export const fmtShare = (part: number, whole: number): string =>
  whole > 0 && Number.isFinite(part / whole)
    ? fmtPct((part / whole) * 100)
    : DASH;

export interface Delta {
  label: string;
  direction: 'up' | 'down' | 'flat';
}

/** "▲ 12%" / "▼ 8%" / "0%"; null delta (prior span empty) → "n/a". */
export const fmtDelta = (deltaPct: number | null | undefined): Delta => {
  if (typeof deltaPct !== 'number' || !Number.isFinite(deltaPct))
    return { label: 'n/a', direction: 'flat' };
  const rounded = Math.round(deltaPct);
  if (rounded === 0) return { label: '0%', direction: 'flat' };
  return rounded > 0
    ? { label: `▲ ${rounded}%`, direction: 'up' }
    : { label: `▼ ${Math.abs(rounded)}%`, direction: 'down' };
};

/** Percentage change of `now` vs `before`; null when `before` is 0. */
export const deltaPct = (now: number, before: number): number | null =>
  before > 0 ? ((now - before) / before) * 100 : null;

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/** 'YYYY-MM-DD' → "14 Sep" (UTC). */
export const fmtWeekStart = (iso: string): string => {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]}`;
};

/** ISO week Mon..Sun as "7–13 Sep" (or "28 Sep – 4 Oct" across months). */
export const fmtWeekRange = (weekStartIso: string): string => {
  const start = new Date(`${weekStartIso}T00:00:00Z`);
  if (Number.isNaN(start.getTime())) return weekStartIso;
  const end = new Date(start.getTime() + 6 * 86_400_000);
  const sameMonth = start.getUTCMonth() === end.getUTCMonth();
  return sameMonth
    ? `${start.getUTCDate()}–${end.getUTCDate()} ${MONTHS[start.getUTCMonth()]}`
    : `${fmtWeekStart(weekStartIso)} – ${end.getUTCDate()} ${
        MONTHS[end.getUTCMonth()]
      }`;
};

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** "Mon 14 Sep → Thu 17 Sep 08:55 UTC" for the current-week banner. */
export const fmtSpan = (startIso: string, throughIso: string): string => {
  const start = new Date(`${startIso}T00:00:00Z`);
  const through = new Date(throughIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(through.getTime()))
    return DASH;
  const hh = String(through.getUTCHours()).padStart(2, '0');
  const mm = String(through.getUTCMinutes()).padStart(2, '0');
  return `${DAYS[start.getUTCDay()]} ${fmtWeekStart(startIso)} → ${
    DAYS[through.getUTCDay()]
  } ${through.getUTCDate()} ${MONTHS[through.getUTCMonth()]} ${hh}:${mm} UTC`;
};

/** "Mon→Thu" — the weekday span used for the same-span comparison. */
export const fmtWeekdaySpan = (throughIso: string): string => {
  const through = new Date(throughIso);
  if (Number.isNaN(through.getTime())) return DASH;
  return `Mon→${DAYS[through.getUTCDay()]}`;
};
